import React, { useEffect } from 'react'
import { useSession } from 'src/sdk/session'
import { useInView } from 'react-intersection-observer'
import { usePromoView } from 'src/sdk/analytics/hooks/usePromoViewEvent'
import { usePromoClick } from 'src/sdk/analytics/hooks/usePromoClickEvent'
import { getCookie, setCookie } from 'src/utils/cookie'
import { Image } from 'src/components/ui/Image'
import Link from 'src/components/ui/Link'

import type { CarouselProps } from './AnimatedCarousel'

type CarouselItemProps = {
  item: CarouselProps
  index: number
  sizings: {
    imageWidth: number
    imageHeight: number
  }
  imageLoading: 'lazy' | 'eager' | undefined
  labelBackground: string
  labelTextColor: string
  onPromoView: (promotionId: string) => void
  promoViewEventTriggered: Set<string>
}

const AnimatedCarouselItem = ({
  item,
  sizings,
  imageLoading,
  labelBackground,
  labelTextColor,
  onPromoView,
  promoViewEventTriggered,
}: CarouselItemProps) => {
  const { person } = useSession()
  const { sendPromoViewEvent } = usePromoView()
  const { sendPromoClickEvent } = usePromoClick()
  const uuidToUse = getCookie('uuid')

  const { ref: inViewRef, inView } = useInView({
    triggerOnce: true,
    threshold: 0.75,
  })

  const widthImage = sizings?.imageWidth || 405
  const imageHeight = sizings?.imageHeight || 320

  const {
    promotion,
    campaignEco,
    promotionId,
    promotionCategory,
    promotionName,
    promotionPosition,
  } = item

  useEffect(() => {
    if (
      promotion &&
      inView &&
      promotionId &&
      !promoViewEventTriggered.has(promotionId)
    ) {
      sendPromoViewEvent({
        campaign_eco: campaignEco,
        promotion_id: promotionId as string,
        promotion_category: promotionCategory as string,
        promotion_name: promotionName as string,
        promotion_creative: item.alt,
        promotion_position: promotionPosition as string,
        page_location: window.location.href,
        user_logged: !!person?.id,
        user_id: person?.id ?? null,
        uuid: uuidToUse,
      })

      if (campaignEco) {
        setCookie('campanhaEco', campaignEco, 7, '.vivara.com.br')
      }

      onPromoView(promotionId)
    }
  }, [inView, promotion, promoViewEventTriggered, promotionId, onPromoView])

  return (
    <Link
      to={item.href}
      className="animted-carousel-link"
      onClick={() => {
        if (promotion) {
          sendPromoClickEvent({
            campaign_eco: campaignEco,
            promotion_id: promotionId as string,
            promotion_category: promotionCategory as string,
            promotion_name: promotionName as string,
            promotion_creative: item.alt,
            position: promotionPosition as string,
            user_logged: !!person?.id,
            user_id: person?.id ?? null,
            uuid: uuidToUse,
          })

          if (campaignEco) {
            setCookie('campanhaEco', campaignEco, 7, '.vivara.com.br')
          }
        }
      }}
    >
      <div className="slider-items" ref={inViewRef}>
        <Image
          baseUrl={item.src}
          alt={item.alt}
          aspectRatio={imageHeight / widthImage}
          height={imageHeight}
          width={widthImage}
          layout="constrained"
          backgroundColor="#F0F0F0"
          loading={imageLoading}
          objectFit="cover"
        />
        <div
          className="image-label"
          style={{ backgroundColor: labelBackground }}
        >
          <p style={{ color: labelTextColor }}>{item?.label}</p>
        </div>
      </div>
    </Link>
  )
}

export default AnimatedCarouselItem
